import { useEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { Alert, Box, Button, CircularProgress, Container, Paper, Stack, Typography } from '@mui/material';
import { GroupsRounded } from '@mui/icons-material';
import OnboardingClient from '../apiClients/OnboardingClient';
import { useAccessInfo } from '../contexts/AccessTokenProvider';

const AcceptSetupInvitationPage = () => {
    const location = useLocation();
    const history = useHistory();
    const { isLoggedIn, startSetupInvitation, reloadAfterInvitationAccepted } = useAccessInfo();
    const loggedIn = isLoggedIn();
    const invitationId = useMemo(() => new URLSearchParams(location.search).get('id') ?? '', [location.search]);
    const validId = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(invitationId);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(() => new URLSearchParams(location.search).get('auth') === 'cancelled' ? 'Secure sign-in was canceled safely. Use the identity connected to the invitation when you retry.' : '');
    const attemptedInvitation = useRef('');

    useEffect(() => {
        if (!validId || !loggedIn || attemptedInvitation.current === invitationId)
            return;
        attemptedInvitation.current = invitationId;
        let active = true;
        setBusy(true);
        new OnboardingClient().AcceptCollaborator(invitationId)
            .then(result => reloadAfterInvitationAccepted(result.OrganizationId))
            .then(switched => {
                if (!switched)
                    throw new Error('Organization access is not ready.');
                if (active)
                    history.replace('/');
            })
            .catch(() => {
                if (active)
                    setError('We could not accept this invitation. Confirm you signed in with the email address that received it, then retry.');
            })
            .finally(() => active && setBusy(false));
        return () => { active = false; };
    }, [history, invitationId, loggedIn, reloadAfterInvitationAccepted, validId]);

    if (!validId)
        return <Container maxWidth="sm" sx={{ py: 8 }}><Alert severity="error">This setup invitation link is invalid.</Alert></Container>;

    return (
        <Container maxWidth="sm" sx={{ py: { xs: 4, sm: 8 }, px: { xs: 2, sm: 3 } }}>
            <Paper variant="outlined" sx={{ p: { xs: 2.5, sm: 4 }, borderRadius: 3 }}>
                <Stack spacing={2.5} alignItems="stretch">
                    <Box sx={{ textAlign: 'center' }}><GroupsRounded color="primary" sx={{ fontSize: 52 }} /><Typography variant="h5" fontWeight={800}>Join as a setup collaborator</Typography><Typography color="text.secondary">Sign in with the invited Microsoft or email identity. This does not create a billable employee record.</Typography></Box>
                    {error && <Alert severity="warning">{error}</Alert>}
                    {busy ? <Box role="status" aria-live="polite" sx={{ display: 'flex', gap: 1.5, justifyContent: 'center', alignItems: 'center', minHeight: 44 }}><CircularProgress size={24} />Verifying invitation…</Box> : !loggedIn ? <Button variant="contained" size="large" onClick={() => startSetupInvitation(invitationId)} sx={{ minHeight: 48 }}>Sign in to accept invitation</Button> : <Button variant="contained" onClick={() => window.location.reload()} sx={{ minHeight: 48 }}>Retry invitation</Button>}
                </Stack>
            </Paper>
        </Container>
    );
};

export default AcceptSetupInvitationPage;
